"use client";

import type React from "react";
import { Box, Text, useToken } from "@chakra-ui/react";
import { Bot } from "lucide-react";
import type { Node } from "./types";

interface FlowNodeProps {
  node: Node;
  selected: boolean;
  onDragStart: (e: React.MouseEvent) => void;
  onDrag: (e: React.MouseEvent) => void;
  onDragEnd: () => void;
  onConnectorMouseDown: (
    nodeId: string,
    point: "top" | "bottom",
    handleIndex: number,
    e: React.MouseEvent
  ) => void;
}

export const FlowNode: React.FC<FlowNodeProps> = ({
  node,
  selected,
  onDragStart,
  onDrag,
  onDragEnd,
  onConnectorMouseDown,
}) => {
  const isStart = node.type === "start";

  // Colors driven purely by Chakra theme
  const cardBg = "white";
  const borderColor = selected ? "gray.300" : "gray.200";
  const hoverBorder = "gray.400";
  const startLabel = "gray.600";
  const titleColor = "gray.800";
  const bodyColor = "gray.600";
  const connectorBg = "gray.500";
  const connectorHover = "gray.600";

  // Selection ring uses theme blue; swap to your brand (e.g., "primary.500") if defined
  const [ringColor, iconGreen] = useToken("colors", ["blue.400", "green.400"]);

  return (
    <Box
      position="relative"
      width="270px"
      data-node-id={node.id}
      onMouseDown={(e) => onDragStart(e)}
      onMouseMove={(e) => onDrag(e)}
      onMouseUp={onDragEnd}
      cursor="move"
      role="group"
      aria-selected={selected}
      borderRadius="md"
    >
      {/* Starting State Label */}
      {isStart && (
        <Text
          position="absolute"
          top="-25px"
          left="0"
          width="100%"
          textAlign="center"
          fontSize="sm"
          color={startLabel}
          pointerEvents="none"
        >
          Starting State
        </Text>
      )}

      {/* Node Card */}
      <Box
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        p={4}
        _hover={{ borderColor: hoverBorder }}
        boxShadow={selected ? `0 0 0 2px ${ringColor}` : "sm"}
        transition="box-shadow 0.2s ease, border-color 0.2s ease"
      >
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <Bot size={20} color={iconGreen} />
          <Text color={titleColor} fontSize="sm" fontWeight="medium">
            {node.data?.state_name || node.id}
          </Text>
        </Box>

        {/* State Prompt Preview */}
        {node.data?.state_prompt && (
          <Text color={bodyColor} fontSize="xs" noOfLines={2}>
            {node.data.state_prompt}
          </Text>
        )}
      </Box>

      {/* Connectors */}
      <Box
        position="absolute"
        top="-15px"
        left="50%"
        transform="translateX(-50%)"
        width="30px"
        height="30px"
        cursor="pointer"
        data-connector="top"
        data-handle-index="0"
        onMouseDown={(e) => onConnectorMouseDown(node.id, "top", 0, e)}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          width="10px"
          height="10px"
          borderRadius="full"
          bg={connectorBg}
          _hover={{ bg: connectorHover }}
        />
      </Box>

      <Box
        position="absolute"
        bottom="-15px"
        left="50%"
        transform="translateX(-50%)"
        width="30px"
        height="30px"
        cursor="pointer"
        data-connector="bottom"
        data-handle-index="0"
        onMouseDown={(e) => onConnectorMouseDown(node.id, "bottom", 0, e)}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          width="10px"
          height="10px"
          borderRadius="full"
          bg={connectorBg}
          _hover={{ bg: connectorHover }}
        />
      </Box>
    </Box>
  );
};
